import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, App, AutoComplete, Button, DatePicker, Form, Input, Radio } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DOLayout from '../../components/directOrders/DOLayout';
import { Reference, StatusBadge } from '../../components/cfq/CfqParts';
import cfqService from '../../services/cfqService';
import { openApiFile } from '../../utils/apiAuth';
import { addWorkingDays, formatLong, isWorkingDay, shiftDays, weekdayShort, workingDaysBetween } from '../../utils/workingDays';

// The same limits the backend enforces. The number is valid until the call closes.
const MIN_CLOSING = 5;
const MAX_CLOSING = 15;

const Section = ({ title, hint, children }) => (
  <section className="do-form-section">
    <div>
      <h2 className="do-form-section__title">{title}</h2>
      {hint && <p className="do-form-section__hint">{hint}</p>}
    </div>
    <div>{children}</div>
  </section>
);

/** Issue date to the closing date, one tile per day. */
const WorkingDays = ({ issue, closing, holidays }) => {
  const days = [];
  let counted = 0;
  for (let date = issue; date <= shiftDays(closing, 1); date = shiftDays(date, 1)) {
    const working = isWorkingDay(date, holidays);
    const weekend = !working && !holidays.has(date);
    if (date > issue && working) counted += 1;
    let label = '';
    let kind = '';
    if (date === issue) [label, kind] = ['Issue', 'issue'];
    else if (holidays.has(date)) [label, kind] = ['Holiday', 'holiday'];
    else if (weekend) kind = 'weekend';
    else if (date <= closing) [label, kind] = [`Day ${counted}`, 'counted'];
    if (date === closing) label = 'Closes';
    days.push({ date, label, className: `cfq-day${kind ? ` cfq-day--${kind}` : ''}${date === closing ? ' cfq-day--end' : ''}` });
  }
  const holidayNames = days.filter((d) => holidays.has(d.date) && d.date !== issue).map((d) => formatLong(d.date));

  return (
    <>
      <div className="cfq-days" role="list" aria-label="Working days from the issue date">
        {days.map((d) => (
          <div key={d.date} className={d.className} role="listitem" aria-label={`${formatLong(d.date)}${d.label ? `, ${d.label}` : ''}`}>
            <span className="cfq-day__dow">{weekdayShort(d.date)}</span>
            <span className="cfq-day__num">{Number(d.date.slice(8, 10))}</span>
            <span className="cfq-day__label">{d.label}</span>
          </div>
        ))}
      </div>
      <div className="cfq-key" aria-hidden="true">
        <span><i style={{ background: 'var(--do-accent)', borderColor: 'var(--do-accent)' }} />Issue</span>
        <span><i style={{ background: 'var(--do-accent-tint)', borderColor: '#c9d3fb' }} />Working day</span>
        <span><i style={{ background: 'repeating-linear-gradient(135deg,#fde9cc 0 3px,#fffaf3 3px 6px)', borderColor: '#f6d7ae' }} />Public holiday</span>
        <span><i style={{ background: '#f2f4f7' }} />Weekend</span>
        <span><i style={{ border: '2px solid var(--do-accent)' }} />Closes</span>
      </div>
      {holidayNames.length > 0 && (
        <p className="do-panel__hint" style={{ marginTop: 10 }}>
          {holidayNames.join(' and ')} {holidayNames.length > 1 ? 'are public holidays' : 'is a public holiday'}, so {holidayNames.length > 1 ? 'they aren’t' : 'it isn’t'} counted.
        </p>
      )}
    </>
  );
};

const CfqRequest = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [calendar, setCalendar] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [sop, setSop] = useState(null);
  const [recent, setRecent] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    cfqService
      .holidays()
      .then(({ today, holidays }) => {
        const set = new Set(holidays);
        setCalendar({ today, holidays: set });
        form.setFieldsValue({ issue_date: isWorkingDay(today, set) ? today : addWorkingDays(today, 1, set) });
      })
      .catch((err) => setError(err.message));
    cfqService.festivals().then(setFestivals).catch(() => {});
    cfqService.sop().then(setSop).catch(() => {});
    cfqService.mine().then((mine) => setRecent(mine.slice(0, 3))).catch(() => {});
  }, [form]);

  const issue = Form.useWatch('issue_date', form);
  const closingValue = Form.useWatch('closing_date', form);
  const festivalText = Form.useWatch('festival', form) || '';
  const closing = closingValue ? closingValue.format('YYYY-MM-DD') : null;
  const holidays = calendar?.holidays;
  // Without a closing date the call runs for the maximum, as the backend saves it.
  const closesOn = closing || (issue && holidays ? addWorkingDays(issue, MAX_CLOSING, holidays) : null);

  const issueOptions = useMemo(() => {
    if (!calendar) return [];
    const next = addWorkingDays(calendar.today, 1, calendar.holidays);
    return [
      { value: calendar.today, label: `Today, ${formatLong(calendar.today)}` },
      { value: next, label: `Next working day, ${formatLong(next)}` },
    ];
  }, [calendar]);

  const festivalOptions = useMemo(() => {
    const term = festivalText.trim().toLowerCase();
    return festivals.filter((f) => !term || f.toLowerCase().includes(term)).map((f) => ({ value: f }));
  }, [festivals, festivalText]);

  const closingAllowed = (iso) => {
    if (!issue || !holidays || !isWorkingDay(iso, holidays)) return false;
    const days = workingDaysBetween(issue, iso, holidays);
    return days >= MIN_CLOSING && days <= MAX_CLOSING;
  };

  // A new issue date can put the chosen closing date out of range.
  const onValuesChange = (changed) => {
    if ('issue_date' in changed && closing && !closingAllowed(closing)) form.setFieldsValue({ closing_date: null });
  };

  const submit = async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      const request = await cfqService.create({
        description: values.description,
        festival: values.festival,
        method: values.method,
        issue_date: values.issue_date,
        closing_date: values.closing_date ? values.closing_date.format('YYYY-MM-DD') : null,
      });
      navigate(`/cfq/${request.id}`, { state: { issued: true } });
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const downloadSop = () =>
    openApiFile(cfqService.sopFileUrl, { download: true, filename: sop?.file_name }).catch((err) => message.error(err.message));

  return (
    <DOLayout
      title="Request a CfQ reference"
      subtitle="Fill this in before you send out a call. You get the number straight away, it’s emailed to you, and Administration is told. The number is valid until the call closes."
      back={{ to: '/cfq', label: 'Call for Quote' }}
      showNav={false}
    >
      <div className="do-create">
        <Form form={form} layout="vertical" requiredMark="optional" onFinish={submit} onValuesChange={onValuesChange} initialValues={{ method: 'Email' }}>
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 24 }} />}
          <Section title="What is the call for?">
            <Form.Item
              name="description"
              label="Description of the services, works or supplies"
              rules={[{ required: true, whitespace: true, message: 'Describe what the call is for' }]}
            >
              <Input.TextArea rows={3} maxLength={1000} placeholder="e.g. Photography services for Mużika Mużika 2027" />
            </Form.Item>
            <Form.Item
              name="festival"
              label="Festival or event"
              extra="Pick from the list, or type a name that isn’t there. A typed name is only saved on this CfQ."
              rules={[{ required: true, whitespace: true, message: 'Pick or type the festival or event' }]}
            >
              <AutoComplete options={festivalOptions} maxLength={255} placeholder="Start typing to search" />
            </Form.Item>
            <Form.Item name="method" label="How will you send the call?" rules={[{ required: true, message: 'Choose how the call will be sent' }]}>
              <Radio.Group optionType="button" buttonStyle="solid" options={['Email', 'ePPs']} />
            </Form.Item>
          </Section>

          <Section title="When does it run?" hint="Weekends and public holidays don’t count as working days.">
            <Form.Item name="issue_date" label="Issue date" rules={[{ required: true, message: 'Choose the issue date' }]}>
              <Radio.Group options={issueOptions} style={{ display: 'flex', flexDirection: 'column', gap: 8 }} />
            </Form.Item>
            <Form.Item
              name="closing_date"
              label="Closing date"
              extra={`Optional. Between ${MIN_CLOSING} and ${MAX_CLOSING} working days after the issue date. Left empty, the call closes after ${MAX_CLOSING} working days.`}
            >
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: '100%', maxWidth: 260 }}
                disabled={!issue}
                disabledDate={(d) => !closingAllowed(d.format('YYYY-MM-DD'))}
                defaultPickerValue={issue && holidays ? dayjs(addWorkingDays(issue, MIN_CLOSING, holidays)) : undefined}
              />
            </Form.Item>
          </Section>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 8 }}>
            <Button type="primary" htmlType="submit" loading={submitting} disabled={!calendar}>
              Get reference number
            </Button>
            <Link to="/cfq">
              <Button>Cancel</Button>
            </Link>
          </div>
        </Form>

        <aside className="do-rail" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <section className="do-panel">
            <div className="do-panel__head">
              <div>
                <h2 className="do-panel__title">Working days</h2>
                {closesOn && (
                  <p className="do-panel__hint">
                    {closing
                      ? `The call is open for ${workingDaysBetween(issue, closing, holidays)} working days and closes on ${formatLong(closing)}.`
                      : `No closing date set, so the call is open for the maximum ${MAX_CLOSING} working days and closes on ${formatLong(closesOn)}.`}
                  </p>
                )}
              </div>
            </div>
            <div className="do-panel__body">
              {closesOn ? (
                <WorkingDays issue={issue} closing={closesOn} holidays={holidays} />
              ) : (
                <p className="do-muted" style={{ margin: 0 }}>Loading the calendar…</p>
              )}
            </div>
          </section>

          <section className="do-panel">
            <div className="do-panel__head">
              <h2 className="do-panel__title">Before you send the call</h2>
            </div>
            <div className="do-panel__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ margin: 0 }}>
                <strong>Your number is valid until the call closes{closesOn ? `, on ${formatLong(closesOn)}` : ''}.</strong>
              </p>
              <p style={{ margin: 0 }}>If the call is cancelled later, cancel it here so Administration knows.</p>
              {sop && (
                <>
                  <p style={{ margin: 0 }}>The SOP for Incoming Invoices/Estimates is here if you need it.</p>
                  <Button icon={<DownloadOutlined />} onClick={downloadSop} style={{ alignSelf: 'flex-start' }}>
                    Download the SOP
                  </Button>
                </>
              )}
            </div>
          </section>

          {recent.length > 0 && (
            <section className="do-panel">
              <div className="do-panel__head">
                <h2 className="do-panel__title">Your recent CfQs</h2>
              </div>
              <div className="do-panel__body">
                {recent.map((r) => (
                  <Link key={r.id} to={`/cfq/${r.id}`} className="cfq-recent">
                    <span>
                      <Reference request={r} />
                      <span className="cfq-table__festival">{r.description}</span>
                    </span>
                    <StatusBadge status={r.status} />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </DOLayout>
  );
};

export default CfqRequest;
